<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, helpers } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

interface InvestmentPath {
  key: string
  letter: string
  name: string
  description?: string
  hlutabref?: string
  skuldabref?: string
  innlan?: string
}

const investmentPaths = computed<InvestmentPath[]>(() => [
  {
    key: 'aldurstengd',
    letter: 'A',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_aldurstengd'),
    description: t('umsokn_tilgreind_sereign_step3_aldurstengd_lysing'),
  },
  {
    key: 'eign7030',
    letter: 'B',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_eign7030'),
    hlutabref: t('umsokn_tilgreind_sereign_step3_eign7030_hlutabref'),
    skuldabref: t('umsokn_tilgreind_sereign_step3_eign7030_skuldabref'),
  },
  {
    key: 'eign5050',
    letter: 'C',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_eign5050'),
    hlutabref: t('umsokn_tilgreind_sereign_step3_eign5050_hlutabref'),
    skuldabref: t('umsokn_tilgreind_sereign_step3_eign5050_skuldabref'),
  },
  {
    key: 'eign2080',
    letter: 'D',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_eign2080'),
    hlutabref: t('umsokn_tilgreind_sereign_step3_eign2080_hlutabref'),
    skuldabref: t('umsokn_tilgreind_sereign_step3_eign2080_skuldabref'),
  },
  {
    key: 'verdtryggd',
    letter: 'E',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_verdtryggd'),
    innlan: t('umsokn_tilgreind_sereign_step3_verdtryggd_innlan'),
  },
  {
    key: 'erlend',
    letter: 'F',
    name: t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_erlend'),
    hlutabref: t('sereignar_umsokn_step2_erlend_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_erlend_skuldabref'),
  },
])

const selectedPath = computed(() =>
  investmentPaths.value.find(p => p.key === umsokn.value.framtidarsyn)
)

// --- Validation rules ---
const oneOfToggles = (value: boolean) =>
  value === true || umsokn.value.oskaFlutning === true

const oneOfTogglesMirror = (value: boolean) =>
  value === true || umsokn.value.avaxtaIdgjalds === true

const checkedIf = (condition: () => boolean) =>
  helpers.withParams({ type: 'checkedIf' }, (value: boolean) => !condition() || value === true)

const rules = {
  umsokn: {
    framtidarsyn: { required: requiredIf(() => umsokn.value.avaxtaIdgjalds === true) },
    stadfestiFjarfestingastefnu: { checked: checkedIf(() => umsokn.value.avaxtaIdgjalds === true) },
    lykilupplysingarAccepted: { checked: checkedIf(() => umsokn.value.avaxtaIdgjalds === true) },
    avaxtaIdgjalds: { oneOfToggles: helpers.withMessage('toggle', oneOfToggles) },
    oskaFlutning: { oneOfTogglesMirror: helpers.withMessage('toggle', oneOfTogglesMirror) },
    vorsluAdili: {},
    vorsluAdiliSSN: {},
  },
}

const v$ = useVuelidate(rules, { umsokn })

function selectPath(path: InvestmentPath) {
  if (props.disable) return
  umsokn.value.framtidarsyn = path.key
}

function isSelected(path: InvestmentPath) {
  return umsokn.value.framtidarsyn === path.key
}

function clearAvoxtunIdgjalds(nextVal: boolean) {
  if (!nextVal) {
    umsokn.value.framtidarsyn = null
    umsokn.value.stadfestiFjarfestingastefnu = null
    umsokn.value.lykilupplysingarAccepted = false
  } else {
    umsokn.value.oskaFlutning = false
    umsokn.value.vorsluAdiliSSN = null
    umsokn.value.vorsluAdili = null
  }
}

function clearFlutning(nextVal: boolean) {
  if (!nextVal) {
    umsokn.value.vorsluAdiliSSN = null
    umsokn.value.vorsluAdili = null
  } else {
    umsokn.value.avaxtaIdgjalds = false
    umsokn.value.framtidarsyn = null
    umsokn.value.stadfestiFjarfestingastefnu = null
  }
}

onLoaded(() => {
  // Eldri drög vistuðu þýdda heitið í framtidarsyn — normalísera yfir í lykilinn
  const legacy = investmentPaths.value.find(p => p.name === umsokn.value.framtidarsyn)
  if (legacy) {
    umsokn.value.framtidarsyn = legacy.key
  }

  if (!props.disable) {
    umsokn.value.lykilupplysingarAccepted = false
  }
})

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  },
})
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">
          {{ t('umsokn_tilgreind_sereign_step3_header') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Toggle: Avöxtun iðgjalds -->
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="umsokn.avaxtaIdgjalds"
          :label="t('umsokn_tilgreind_sereign_step3_idgjald')"
          :readonly="props.disable"
          density="comfortable"
          @update:modelValue="clearAvoxtunIdgjalds"
          :error="v$.umsokn.avaxtaIdgjalds.$error"
          :error-messages="
            v$.umsokn.avaxtaIdgjalds.$error || v$.umsokn.oskaFlutning.$error
              ? [t('umsokn_tilgreind_sereign_step3_Avoxtun_idgjalds_error')]
              : []
          "
        />
      </v-col>
    </v-row>

    <!-- Avöxtun idgjalds details -->
    <transition name="list">
      <div v-if="umsokn.avaxtaIdgjalds === true" class="indent__drop">

        <!-- Summary mode: show only the selected path -->
        <template v-if="props.disable && selectedPath">
          <div class="path-cards">
            <div class="path-card path-card--selected">
              <div class="path-card__badge">
                <span class="path-card__letter path-card__letter--selected"></span>
              </div>
              <div class="path-card__name-col">
                <span class="path-card__name">{{ selectedPath.name }}</span>
              </div>
              <div class="path-card__middle">
                <span v-if="selectedPath.description" class="path-card__description">{{ selectedPath.description }}</span>
                <div v-else-if="selectedPath.hlutabref || selectedPath.innlan" class="path-card__allocations">
                  <span v-if="selectedPath.hlutabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_hlutabref') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.hlutabref }}</span>
                  </span>
                  <span v-if="selectedPath.skuldabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_skuldabref') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.skuldabref }}</span>
                  </span>
                  <span v-if="selectedPath.innlan" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_innlan') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.innlan }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Edit mode: full selection UI -->
        <template v-else>
        <h3 class="path-section__title">
          {{ t('umsokn_tilgreind_sereign_step3_veldu_fjarfestingarleid') }}
        </h3>
        <p class="path-section__desc">
          {{ t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_intro') }}
        </p>
        <p class="path-section__desc path-section__desc--link">
          {{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_texti') }}
          <a class="link" target="_blank" :href="t('sereignar_umsokn_lykilupplysingar_url')">{{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_link') }}</a>
        </p>

        <!-- Validation error -->
        <p v-if="v$.umsokn.framtidarsyn.$error" class="path-section__error">
          {{ t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_error') }}
        </p>

        <!-- Investment path cards -->
        <div class="path-cards">
          <div
            v-for="path in investmentPaths"
            :key="path.key"
            class="path-card"
            :class="{ 'path-card--selected': isSelected(path) }"
          >
            <div class="path-card__badge">
              <span class="path-card__letter" :class="{ 'path-card__letter--selected': isSelected(path) }"></span>
            </div>
            <div class="path-card__name-col">
              <span class="path-card__name">{{ path.name }}</span>
            </div>
            <div class="path-card__middle">
              <span v-if="path.description" class="path-card__description">{{ path.description }}</span>
              <div v-else-if="path.hlutabref || path.innlan" class="path-card__allocations">
                <span v-if="path.hlutabref" class="path-card__alloc">
                  <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_hlutabref') }}</span>
                  <span class="path-card__alloc-value">{{ path.hlutabref }}</span>
                </span>
                <span v-if="path.skuldabref" class="path-card__alloc">
                  <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_skuldabref') }}</span>
                  <span class="path-card__alloc-value">{{ path.skuldabref }}</span>
                </span>
                <span v-if="path.innlan" class="path-card__alloc">
                  <span class="path-card__alloc-label">{{ t('umsokn_tilgreind_sereign_step3_innlan') }}</span>
                  <span class="path-card__alloc-value">{{ path.innlan }}</span>
                </span>
              </div>
            </div>
            <div class="path-card__action">
              <button
                class="path-card__btn"
                :class="{ 'path-card__btn--active': isSelected(path) }"
                :disabled="props.disable"
                @click="selectPath(path)"
              >
                {{ isSelected(path) ? t('umsokn_tilgreind_sereign_step3_valin_leid') : t('umsokn_tilgreind_sereign_step3_velja_leid') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Confirm investment policy -->
        <v-row v-if="umsokn.framtidarsyn" class="mt-4">
          <v-col cols="12">
            <v-checkbox
              v-model="umsokn.stadfestiFjarfestingastefnu"
              :readonly="props.disable"
              density="comfortable"
              :label="t('umsokn_tilgreind_sereign_step3_confirm')"
              :error="v$.umsokn.stadfestiFjarfestingastefnu.$error"
              :error-messages="
                v$.umsokn.stadfestiFjarfestingastefnu.$error
                  ? [t('validation_required')]
                  : []
              "
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="umsokn.lykilupplysingarAccepted"
              :readonly="props.disable"
              density="comfortable"
              :error="v$.umsokn.lykilupplysingarAccepted.$error"
              :error-messages="
                v$.umsokn.lykilupplysingarAccepted.$error
                  ? [t('validation_required')]
                  : []
              "
            >
              <template #label>
                <a class="link" target="_blank" :href="t('sereignar_umsokn_lykilupplysingar_url')">
                  {{ t('sereignar_umsokn_lykilupplysingar') }}
                </a>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        </template>
      </div>
    </transition>

    <!-- Toggle: Óska eftir flutning -->
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="umsokn.oskaFlutning"
          :label="t('umsokn_tilgreind_sereign_step3_oska_flutning')"
          :readonly="props.disable"
          density="comfortable"
          @update:modelValue="clearFlutning"
          :error="v$.umsokn.oskaFlutning.$error"
          :error-messages="
            v$.umsokn.oskaFlutning.$error || v$.umsokn.avaxtaIdgjalds.$error
              ? [t('umsokn_tilgreind_sereign_step3_Avoxtun_idgjalds_error')]
              : []
          "
        />
      </v-col>
    </v-row>

    <!-- Flutningur details -->
    <transition name="list">
      <v-row class="indent__drop" v-if="umsokn.oskaFlutning === true">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.vorsluAdili"
            :label="t('umsokn_tilgreind_sereign_step3_nafn')"
            :readonly="props.disable"
            variant="outlined"
            density="comfortable"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.vorsluAdiliSSN"
            :label="t('umsokn_tilgreind_sereign_step3_kennitala')"
            :readonly="props.disable"
            variant="outlined"
            density="comfortable"
            inputmode="numeric"
          />
        </v-col>
        <v-col cols="12">
          <p v-html="t('umsokn_tilgreind_sereign_step3_aretting')"></p>
        </v-col>
      </v-row>
    </transition>

    <!-- Disclaimer link -->
    <div v-if="!props.disable" class="disclamer">
      <a class="link" :href="t('sereignar_umsokn_disclamer_url')">
        {{ t('sereignar_umsokn_disclamer') }}
      </a>
    </div>

  </div>
</template>

<style lang="scss" scoped>
@use "@/scss/settings/variables" as *;

input {
  width: 100%;
}

.link {
  color: $color-primary;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

.disclamer {
  margin-top: 30px;
  font-size: 18px;
  line-height: 34px;
}

/* ── Path section ── */
.path-section__title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  color: $color-text;
}

.path-section__desc {
  font-size: 14px;
  line-height: 1.6;
  color: $color-text-muted;
  margin-bottom: 4px;

  &--link { margin-bottom: 20px; }
}

.path-section__error {
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
}

/* ── Card list ── */
.path-cards {
  display: flex;
  flex-direction: column;
}

.path-card {
  display: grid;
  grid-template-columns: 48px 180px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid $color-border;
  transition: background-color 0.15s ease;

  &:first-child { border-top: 1px solid $color-border; }
  &:hover { background-color: rgba($color-primary, 0.05); }
  &--selected {
    background-color: rgba($color-primary, 0.10);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10), 0 0 0 1px rgba($color-primary, 0.25);
    border-radius: 8px;
    padding: 20px 12px;
  }
}

.path-card__badge {
  display: flex;
  justify-content: center;
}

.path-card__letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-color: $color-secondary;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  transition: background-color 0.15s ease;

  &--selected {
    background-color: $color-primary;
  }
}

.path-card__name-col {
  display: flex;
  align-items: center;
}

.path-card__name {
  font-weight: 700;
  font-size: 16px;
  color: $color-secondary;
}

.path-card__middle {
  display: flex;
  align-items: center;
  min-height: 48px;
}

.path-card__description {
  font-size: 14px;
  color: $color-text-muted;
  line-height: 1.5;
  font-style: italic;
}

.path-card__allocations {
  display: flex;
  gap: 32px;
}

.path-card__alloc {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 72px;
}

.path-card__alloc-label {
  font-size: 11px;
  color: $color-text-muted;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
}

.path-card__alloc-value {
  font-size: 17px;
  font-weight: 700;
  color: $color-primary;
}

.path-card__action {
  display: flex;
  justify-content: flex-end;
}

.path-card__btn {
  white-space: nowrap;
  padding: 10px 28px;
  font-size: 15px;
  font-weight: 600;
  border: 1.5px solid $color-primary;
  border-radius: 6px;
  background: transparent;
  color: $color-primary;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 120px;
  text-align: center;

  &:hover:not(:disabled) {
    background-color: $color-primary;
    color: #fff;
  }

  &--active {
    background-color: $color-primary;
    color: #fff;
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ── Dark mode ── */
:global(.dark-mode) {
  .path-section__title { color: $color-text-dark; }
  .path-section__desc { color: $color-text-muted-dark; }
  .path-card {
    border-color: $color-border-dark;
    &:hover { background-color: rgba($color-primary-dark, 0.08); }
    &--selected {
      background-color: rgba($color-primary-dark, 0.14);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba($color-primary-dark, 0.3);
      border-radius: 8px;
      padding: 20px 12px;
    }
  }
  .path-card__letter { background-color: $color-primary-dark; color: $color-background-dark; }
  .path-card__letter--selected { background-color: $color-primary-dark; }
  .path-card__name { color: $color-text-dark; }
  .path-card__description { color: $color-text-muted-dark; }
  .path-card__alloc-label { color: $color-text-muted-dark; }
  .path-card__alloc-value { color: $color-text-dark; }
  .path-card__btn {
    border-color: $color-primary-dark;
    color: $color-primary-dark;
    &:hover:not(:disabled) { background-color: $color-primary-dark; color: $color-background-dark; }
    &--active { background-color: $color-primary-dark; color: $color-background-dark; }
  }
  .link { color: $color-primary-dark; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .path-section__title {
    text-align: center;
  }

  .path-section__desc {
    text-align: center;
  }

  .path-card {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px 12px;
    padding: 16px 0;
  }

  .path-card__badge {
    order: 1;
    flex-shrink: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .path-card__name-col {
    order: 2;
    width: 100%;
    text-align: center;
  }

  .path-card__name {
    display: block;
    width: 100%;
    text-align: center;
  }

  .path-card__middle {
    order: 3;
    width: 100%;
    min-height: auto;
    display: flex;
    justify-content: center;
  }

  .path-card__description {
    text-align: center;
  }

  .path-card__allocations {
    justify-content: center;
    gap: 20px;
  }

  .path-card__action {
    order: 4;
    width: 100%;
  }

  .path-card__btn {
    width: 100%;
  }
}
</style>
